import { useEffect, useRef, useState } from 'react'
import { api, upload } from '../api/client.ts'
import { Avatar } from '../components/Avatar.tsx'
import { SharePanel } from '../components/SharePanel.tsx'
import { Icon } from '../components/Icon.tsx'

/**
 * ДОКУМЕНТЫ И СРОКИ
 *
 * Раздел нужен ради одного вопроса: что скоро истекает. Поэтому
 * истекающее стоит наверху и подсвечено, а всё остальное — просто
 * списком.
 *
 * Документы личные: чужие не показываются вовсе, пока владелец
 * не откроет их сам. Это видно и в интерфейсе — у своих есть кнопка
 * «кому показать», у чужих её нет.
 */

type FileInfo = {
  id: string
  fileName: string
  mimeType: string
  url: string
  thumbUrl: string | null
}

type Document = {
  id: string
  kind: string
  title: string
  number: string | null
  issuedBy: string | null
  issuedOn: string | null
  expiresOn: string | null
  note: string | null
  owner: { id: string; displayName: string; avatarUrl: string | null }
  mine: boolean
  expired: boolean
  expiringSoon: boolean
  files: FileInfo[]
}

const KINDS: Record<string, string> = {
  PASSPORT: 'Паспорт',
  FOREIGN_PASSPORT: 'Загранпаспорт',
  DRIVER_LICENSE: 'Права',
  INSURANCE: 'Страховка',
  OSAGO: 'ОСАГО',
  MEDICAL_POLICY: 'Медполис',
  CERTIFICATE: 'Свидетельство',
  CONTRACT: 'Договор',
  OTHER: 'Другое',
}

const KIND_ICONS: Record<string, string> = {
  PASSPORT: '📕',
  FOREIGN_PASSPORT: '🛂',
  DRIVER_LICENSE: '🚗',
  INSURANCE: '🛡',
  OSAGO: '🚙',
  MEDICAL_POLICY: '🏥',
  CERTIFICATE: '📜',
  CONTRACT: '📄',
  OTHER: '📁',
}

const dateText = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('ru-RU') : ''

/** «через 12 дней», «истёк 3 марта» — срок словами понятнее даты. */
function expiryText(document: Document): string {
  if (!document.expiresOn) return ''
  if (document.expired) return `истёк ${dateText(document.expiresOn)}`

  const days = Math.ceil(
    (new Date(document.expiresOn).getTime() - Date.now()) / 86_400_000,
  )
  if (days === 0) return 'истекает сегодня'
  if (days === 1) return 'истекает завтра'
  if (days <= 30) return `осталось ${days} дн.`
  return `до ${dateText(document.expiresOn)}`
}

export function DocumentsPage() {
  const [documents, setDocuments] = useState<Document[] | null>(null)
  const [draft, setDraft] = useState<Partial<Document> | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = () => {
    api<{ documents: Document[] }>('/api/documents')
      .then((data) => setDocuments(data.documents))
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить'))
  }

  useEffect(load, [])

  const urgent = (documents ?? []).filter((d) => d.expired || d.expiringSoon)
  const rest = (documents ?? []).filter((d) => !d.expired && !d.expiringSoon)

  return (
    <div className="section">
      <header className="section-head">
        <h1>Документы</h1>
        <button
          className="icon-btn"
          onClick={() => setDraft({ kind: 'OTHER', title: '' })}
          aria-label="Добавить"
        >
          <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
            <path d="M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2z" />
          </svg>
        </button>
      </header>

      <div className="section-body scroll">
        {documents?.length === 0 && (
          <p className="empty-hint">
            Документов нет. Сюда складывают паспорта, права, полисы — и приложение
            напомнит, когда срок подойдёт к концу.
          </p>
        )}

        {urgent.length > 0 && (
          <>
            <h3 className="sheet-sub">Скоро истекает</h3>
            {urgent.map((document) => (
              <DocumentRow key={document.id} document={document} onOpen={() => setDraft(document)} />
            ))}
          </>
        )}

        {rest.length > 0 && (
          <>
            {urgent.length > 0 && <h3 className="sheet-sub">Остальное</h3>}
            {rest.map((document) => (
              <DocumentRow key={document.id} document={document} onOpen={() => setDraft(document)} />
            ))}
          </>
        )}

        {documents && documents.length > 0 && (
          <p className="sheet-note">
            Чужие документы здесь не показываются — только те, что тебе открыли.
          </p>
        )}

        {error && <div className="sheet-error">{error}</div>}
      </div>

      {draft && (
        <DocumentSheet
          draft={draft}
          onClose={() => setDraft(null)}
          onSaved={load}
        />
      )}
    </div>
  )
}

function DocumentRow({
  document,
  onOpen,
}: {
  document: Document
  onOpen: () => void
}) {
  return (
    <button
      className={
        'record-row' + (document.expired ? ' is-expired' : document.expiringSoon ? ' is-soon' : '')
      }
      onClick={onOpen}
    >
      <span className="ticket-icon" aria-hidden="true">
        {KIND_ICONS[document.kind] ?? '📁'}
      </span>

      <span className="record-text">
        <span className="record-title">{document.title}</span>
        <span className="record-sub">
          <span>{KINDS[document.kind] ?? document.kind}</span>
          {document.expiresOn && (
            <span className={document.expired || document.expiringSoon ? 'doc-expiry' : ''}>
              {expiryText(document)}
            </span>
          )}
          {document.files.length > 0 && <span>📎 {document.files.length}</span>}
        </span>
      </span>

      {!document.mine && (
        <Avatar
          id={document.owner.id}
          name={document.owner.displayName}
          url={document.owner.avatarUrl}
          size={22}
        />
      )}
    </button>
  )
}

function DocumentSheet({
  draft,
  onClose,
  onSaved,
}: {
  draft: Partial<Document>
  onClose: () => void
  onSaved: () => void
}) {
  const editing = Boolean(draft.id)
  const readOnly = editing && draft.mine === false

  const [kind, setKind] = useState(draft.kind ?? 'OTHER')
  const [title, setTitle] = useState(draft.title ?? '')
  const [number, setNumber] = useState(draft.number ?? '')
  const [issuedBy, setIssuedBy] = useState(draft.issuedBy ?? '')
  const [issuedOn, setIssuedOn] = useState(draft.issuedOn?.slice(0, 10) ?? '')
  const [expiresOn, setExpiresOn] = useState(draft.expiresOn?.slice(0, 10) ?? '')
  const [note, setNote] = useState(draft.note ?? '')
  const [files, setFiles] = useState<FileInfo[]>(draft.files ?? [])

  const [id, setId] = useState(draft.id)
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [sharing, setSharing] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const save = async () => {
    setBusy(true)
    setError(null)

    try {
      const body = {
        kind,
        title: title.trim(),
        number: number.trim() || null,
        issuedBy: issuedBy.trim() || null,
        issuedOn: issuedOn || null,
        expiresOn: expiresOn || null,
        note: note.trim() || null,
      }

      if (id) {
        await api(`/api/documents/${id}`, { method: 'PATCH', body })
      } else {
        const made = await api<{ document: { id: string } }>('/api/documents', {
          method: 'POST',
          body,
        })
        // Не закрываем лист: скан прикладывается к уже созданной записи,
        // и человеку логично сделать это сразу
        setId(made.document.id)
      }

      onSaved()
      if (id) onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не сохранилось')
    } finally {
      setBusy(false)
    }
  }

  const addFile = async (file: File) => {
    if (!id) {
      setError('Сначала сохрани документ — тогда к нему можно приложить скан')
      return
    }

    setBusy(true)
    setProgress(0)
    try {
      const result = await upload<{ attachments: FileInfo[] }>(
        `/api/attachments/DOCUMENT/${id}`,
        (() => {
          const form = new FormData()
          form.append('files', file, file.name)
          return form
        })(),
        setProgress,
      )
      setFiles((current) => [...current, ...result.attachments])
      onSaved()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Файл не загрузился')
    } finally {
      setBusy(false)
      setProgress(null)
    }
  }

  const dropFile = async (fileId: string) => {
    setFiles((current) => current.filter((f) => f.id !== fileId))
    await api(`/api/attachments/${fileId}`, { method: 'DELETE' })
    onSaved()
  }

  const remove = async () => {
    if (!id || !window.confirm('Удалить документ вместе со сканами?')) return
    setBusy(true)
    try {
      await api(`/api/documents/${id}`, { method: 'DELETE' })
      onSaved()
      onClose()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="sheet" onClick={onClose}>
      <div className="sheet-body" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h2>{editing ? title || 'Документ' : 'Новый документ'}</h2>
          {id && !readOnly && (
            <button className="icon-btn" onClick={() => setSharing(true)} title="Кому показать">
              <Icon name="family" size={21} />
            </button>
          )}
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </div>

        {readOnly ? (
          <>
            <p className="sheet-text">
              {KINDS[kind] ?? kind}
              {draft.owner && ` · ${draft.owner.displayName}`}
            </p>
            {number && <p className="sheet-text">№ {number}</p>}
            {expiresOn && (
              <p className="sheet-text">Срок: {new Date(expiresOn).toLocaleDateString('ru-RU')}</p>
            )}
            {note && <p className="sheet-note">{note}</p>}
          </>
        ) : (
          <>
            <label className="field">
              <span className="field-label">Что это</span>
              <select className="input" value={kind} onChange={(e) => setKind(e.target.value)}>
                {Object.entries(KINDS).map(([value, name]) => (
                  <option key={value} value={value}>
                    {name}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span className="field-label">Название</span>
              <input
                className="input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="ОСАГО на Ладу, паспорт Ани"
                maxLength={200}
                autoFocus={!editing}
              />
            </label>

            <label className="field">
              <span className="field-label">Номер</span>
              <input
                className="input"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                placeholder="Не обязательно"
                maxLength={100}
              />
            </label>

            <div className="amount-row">
              <label className="field">
                <span className="field-label">Выдан</span>
                <input
                  className="input"
                  type="date"
                  value={issuedOn}
                  onChange={(e) => setIssuedOn(e.target.value)}
                />
              </label>
              <label className="field">
                <span className="field-label">Действует до</span>
                <input
                  className="input"
                  type="date"
                  value={expiresOn}
                  onChange={(e) => setExpiresOn(e.target.value)}
                />
              </label>
            </div>
            <p className="sheet-note">
              О сроке напомним трижды: за месяц, за неделю и в день.
            </p>

            <label className="field">
              <span className="field-label">Кем выдан</span>
              <input
                className="input"
                value={issuedBy}
                onChange={(e) => setIssuedBy(e.target.value)}
                maxLength={200}
              />
            </label>

            <label className="field">
              <span className="field-label">Заметка</span>
              <input
                className="input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={1000}
              />
            </label>
          </>
        )}

        <h3 className="sheet-sub">Сканы</h3>
        <div className="record-files">
          {files.map((file) => (
            <div key={file.id} className="record-file">
              {file.thumbUrl ? (
                <a href={file.url} target="_blank" rel="noreferrer">
                  <img src={file.thumbUrl} alt="" />
                </a>
              ) : (
                <a className="record-file-doc" href={file.url} target="_blank" rel="noreferrer">
                  <span aria-hidden="true">📄</span>
                  <span className="record-file-name">{file.fileName}</span>
                </a>
              )}

              {!readOnly && (
                <button
                  className="item-photo-remove"
                  onClick={() => void dropFile(file.id)}
                  aria-label="Убрать скан"
                >
                  ✕
                </button>
              )}
            </div>
          ))}

          {!readOnly && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="image/*,application/pdf"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) void addFile(file)
                  e.target.value = ''
                }}
              />
              <button
                className="item-photo-add"
                onClick={() => fileRef.current?.click()}
                disabled={busy}
              >
                {progress === null ? '+ скан' : `${progress}%`}
              </button>
            </>
          )}
        </div>

        {!readOnly && (
          <p className="sheet-note">
            Скан виден только тебе, пока ты сам не откроешь документ кому-то.
          </p>
        )}

        {error && <div className="sheet-error">{error}</div>}

        {!readOnly && (
          <div className="sheet-actions">
            <button
              className="btn-primary"
              disabled={busy || !title.trim()}
              onClick={() => void save()}
            >
              {busy ? '…' : id ? 'Сохранить' : 'Создать'}
            </button>
            {id && (
              <button className="btn-quiet" disabled={busy} onClick={() => void remove()}>
                Удалить
              </button>
            )}
          </div>
        )}

        {sharing && id && (
          <SharePanel
            entityType="DOCUMENT"
            entityId={id}
            onClose={() => setSharing(false)}
            title="Кому показать документ"
          />
        )}
      </div>
    </div>
  )
}
